import graphql__8fa9c25048b0fa5e4d4b from "./__generated__/MapBase_map.graphql.js";import { useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import { MapContainer, TileLayer } from "react-leaflet";

import getMapConfiguration from "../Utils/getMapConfiguration";
import { useFormatMessage } from "../Intl/Context";
import { useResponsiveFlag } from "../Utils/ResponsiveQueries";
import { useMapContext } from "./MapContext";

import { setMapViewport } from "../../reducers/map/viewport";
import MapElementsVectorGrid from "./MapElementsVectorGrid/MapElementsVectorGrid";
import MapEventsHandler from "./MapEventsHandler";

const MapBase = ({ staticMap = false, mapProps: inputMapProps = {}, ...props }) => {
  const map = useFragment(MapBase_map, props.map);
  const { mapKey } = useMapContext() || {};

  const { children, height, onElementClick } = props;
  const centerLatitude = props.centerLatitude || map.centerLatitude;
  const centerLongitude = props.centerLongitude || map.centerLongitude;

  const t = useFormatMessage();
  const touchGestureLabel = t(intlMessages.touchGesture);
  const scrollGestureLabel = t(intlMessages.scrollGesture);
  const scrollMacGestureLabel = t(intlMessages.scrollMacGesture);
  const ariaLabel = t(intlMessages.ariaLabel);

  const { isDesktop } = useResponsiveFlag();

  const iFrameMode = useSelector(({ styling }) => styling.iFrameMode);

  const desktopZoomDefault = props.zoomDefault || map?.zoomDefault;
  const desktopZoomMin = Math.min(map?.zoomMin, desktopZoomDefault);
  const desktopZoomMax = Math.max(map?.zoomMax, desktopZoomDefault);

  const mobileZoomDefault = map?.mobileZoomDefault || desktopZoomDefault;
  const mobileZoomMin = Math.min(map?.mobileZoomMin || desktopZoomMin, mobileZoomDefault);
  const mobileZoomMax = Math.max(map?.mobileZoomMax || desktopZoomMax, mobileZoomDefault);

  const mapContainerRef = useRef(null);

  const zoomConfig = isDesktop
    ? {
        zoomDefault: desktopZoomDefault,
        zoomMin: desktopZoomMin,
        zoomMax: desktopZoomMax
      }
    : {
        zoomDefault: mobileZoomDefault,
        zoomMin: mobileZoomMin,
        zoomMax: mobileZoomMax
      };
  const { zoomDefault, zoomMin, zoomMax } = zoomConfig;

  const viewportZoom = useSelector(({ map }) => map[mapKey].viewport?.zoom);
  const currentZoomLevel = viewportZoom || props.zoom || zoomDefault;

  const dispatch = useDispatch();

  // TODO: Verify if this is still needed with React 19.
  // Cleanup on unmount to destroy the Leaflet map if it exists
  // This approach ensures the container is fully freed before remounting and prevents
  // "Map container is already initialized" errors. Leaflet internally uses this ID to
  // track the container on the DOM, which React and Leaflet both do not reset
  // automatically when remounting.
  useEffect(() => {
    return () => {
      if (mapContainerRef.current) {
        mapContainerRef.current.remove();
        mapContainerRef.current = null;
      }

      const container = document.querySelector(".leaflet-container");
      if (container && container._leaflet_id) {
        container._leaflet_id = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!map) return;

    dispatch(
      setMapViewport(mapKey, {
        lat: centerLatitude,
        lng: centerLongitude,
        zoom: currentZoomLevel
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  if (!map) return null;

  // Copy the mapProps argument to ensure we don't modify the default one when
  // we are in a static map
  const mapProps = { ...inputMapProps };

  // Generate the map global configuration depending on the current zoom level
  const configuration = getMapConfiguration(currentZoomLevel, map);
  const { tileLayerUrl, mapDataAttribution } = configuration;

  const latitude = centerLatitude || map.centerLatitude;
  const longitude = centerLongitude || map.centerLongitude;

  const position = [latitude, longitude];

  // Disable all zoom and panning features for static maps
  if (staticMap) {
    mapProps.zoomControl = false;
    mapProps.boxZoom = false;
    mapProps.doubleClickZoom = false;
    mapProps.dragging = false;
    mapProps.keyboard = false;
    mapProps.scrollWheelZoom = false;
    mapProps.touchZoom = false;
  }

  // Disable default scroll and swipe behavior when intergrated in iFrame
  if (iFrameMode) {
    mapProps.gestureHandling = true;
    mapProps.gestureHandlingOptions = {
      duration: 1000,
      text: {
        touch: touchGestureLabel,
        scroll: scrollGestureLabel,
        scrollMac: scrollMacGestureLabel
      }
    };
  }

  return (
    <div className="w-100 h-100" aria-label={ariaLabel}>
      <MapContainer
        center={position}
        zoom={currentZoomLevel}
        minZoom={zoomMin}
        maxZoom={zoomMax}
        keyboard={false}
        whenCreated={mapInstance => {
          mapContainerRef.current = mapInstance;
        }}
        style={{ height }}
        {...mapProps}
      >
        <MapEventsHandler />

        <TileLayer url={tileLayerUrl} attribution={mapDataAttribution} />

        <MapElementsVectorGrid map={map} onElementClick={onElementClick} />

        {children}
      </MapContainer>
    </div>
  );
};

const MapBase_map = graphql__8fa9c25048b0fa5e4d4b;

export default MapBase;

const intlMessages = defineMessages({
  touchGesture: {
    id: "map.base.touchGesture",
    defaultMessage: "Vous pouvez vous déplacer sur la carte en utilisant deux doigts"
  },
  scrollGesture: {
    id: "map.base.scrollGesture",
    defaultMessage:
      "Vous pouvez zoomer sur la carte à l'aide de ctrl + Molette de défilement"
  },
  scrollMacGesture: {
    id: "map.base.scrollMacGesture",
    defaultMessage:
      "Vous pouvez zoomer sur la carte à l'aide de \u2318 + Molette de défilement"
  },
  ariaLabel: {
    id: "map.base.ariaLabel",
    defaultMessage: "Carte interactive"
  }
});
